<template>
  <div class="user-reset-password-page" v-rect-loading="loading">
    <div class="user-reset-password-header">
      <div class="user-reset-password-title">{{ titleLabel }}</div>
    </div>
    <div class="form-container">
      <WrappedFrom
        ref="form"
        class="reset-password-info"
        label-width="220px"
        :model="userData"
        :formLines="formLines"
      >
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ cancelBtnLabel }}</el-button>
        <el-button class="save-btn" @click="handleSave">{{ saveBtnLabel }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
// import _ from 'lodash'
import { selectLabelI18n, selectFuncLabelI18n } from '@/i18n'
import { mapActions } from 'vuex'
import { mixedRouterPush } from '@/router/routes'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
// import { validatePassword } from '@/utils/validator'
import { passwordEncrypt } from '@/utils/rsa'

export default {
  components: {
    WrappedFrom
  },
  data () {
    return {
      i18nPrefix: 'admin.company.userManagement',
      loading: false,
      validatePassword: false,
      userData: {},
      selectLabelI18n,
      selectFuncLabelI18n
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    accountId () {
      return this.$route.params.accountId
    },
    titleLabel () {
      return this.$t(`${this.i18nPrefix}.label.resetPassword`)
    },
    cancelBtnLabel () {
      return selectLabelI18n(this, 'cancelBtn', 'common.btn.cancel')
    },
    saveBtnLabel () {
      return selectLabelI18n(this, 'saveBtn', 'common.btn.save')
    },
    formLines () {
      const lines = {
        password: {
          label: this.$t(`${this.i18nPrefix}.label.password`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.password`),
          rules: [
            { required: true, message: this.$t(`${this.i18nPrefix}.message.userRequired`), trigger: 'blur' },
            // { validator: validatePassword, trigger: 'blur' },
            { validator: this.validatePasswordSame, trigger: 'blur' }
          ],
          events: {
            input: this.handleInputChange
          }
        },
        confirm_password: {
          label: this.$t(`${this.i18nPrefix}.label.confirmPassword`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.confirmPassword`),
          rules: [
            { required: true, message: this.$t(`${this.i18nPrefix}.message.userRequired`), trigger: 'blur' },
            // { validator: validatePassword, trigger: 'blur' },
            { validator: this.validateConfirmPasswordSame, trigger: 'blur' }
          ],
          events: {
            input: this.handleInputChange
          }
        }
      }
      return lines
    },
    form () {
      const form = this.$refs.form
      return form
    }
  },
  created () {
    this.queryCompany = this.$route.query.company
    this.init()
  },
  methods: {
    ...mapActions('userManagement', ['resetPasswordByHR', 'getResetPasswordPermission']),
    async init () {
      this.loading = true
      const params = {
        company_id: this.queryCompany
      }
      try {
        await this.getResetPasswordPermission({ accountId: this.accountId, params })
      } finally {
        this.loading = false
      }
    },
    async submit () {
      const params = {
        accountId: this.accountId,
        userData: {
          company_id: this.queryCompany,
          password: passwordEncrypt(this.userData.password),
          confirm_password: passwordEncrypt(this.userData.confirm_password)
        }
      }
      const resp = await this.resetPasswordByHR(params)
      if (resp === 'success') {
        this.$message.success(this.$t(`${this.i18nPrefix}.message.optionSuccess`))
        this.handleCancel()
      } else {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.optionField`))
      }
    },
    handleCancel () {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(this.$route.query.back)
      } else {
        this.$router.go(-1)
      }
    },
    async handleSave () {
      if (this.loading) {
        return
      }
      this.loading = true
      try {
        let result = false
        result = await this.form.submit()
        if (!result) {
          return
        }
        await this.submit()
      } finally {
        this.loading = false
      }
    },
    async handleInputChange () {
      if (this.validatePassword && this.userData.confirm_password && this.userData.password) {
        await this.form.submit()
      }
    },
    validatePasswordSame (rule, value, callback) {
      if (this.validatePassword && this.userData.password !== this.userData.confirm_password) {
        callback(new Error(this.$t(`${this.i18nPrefix}.message.confirmPasswordSame`)))
      } else {
        callback()
      }
    },
    validateConfirmPasswordSame (rule, value, callback) {
      this.validatePassword = true
      this.validatePasswordSame(rule, value, callback)
    }
  }
}
</script>

<style lang="scss" scoped>
.user-reset-password-page {
  .user-reset-password-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;

    .user-reset-password-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .form-container {
    width: 606px;
    margin: 30px auto 0;

    .el-form {
      &.asterisk-after {
        ::v-deep {
          .el-form-item {
            &:not(.is-no-asterisk) {
              .el-form-item__label {

                &::after {
                  content: '*';
                  margin: 11px 0 0 3px;
                  color: #f56c6c;
                  line-height: 1;
                  visibility: hidden;
                }
              }
            }

            &.is-required:not(.is-no-asterisk) {
              .el-form-item__label {
                &::before {
                  display: none;
                }

                &::after {
                  visibility: unset;
                }
              }
            }
          }
        }
      }

      &:not(.el-form--label-top) {
        ::v-deep {
          .el-form-item {
            .el-form-item__label {

              .label-inner {
                margin-top: 0;
                line-height: inherit;
              }
            }
          }
        }
      }

      .el-form-item {
        ::v-deep {
          .el-form-item__label {
            padding: 0 40px 0 0;
            color: #606266;
            line-height: 32px;
          }

          .el-form-item__content {
            line-height: 32px;
          }

          .el-input__inner {
            height: 32px;
            line-height: 32px;
            border-radius: 2px;
          }

          .el-select {
            width: 100%;
          }
        }
      }
    }
  }

  .bottom-btn-group {
    display: flex;
    justify-content: flex-end;

    .el-button {
      padding: 8px 28px;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      font-weight: 500;
      color: #0096cc;

      &:last-child {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}
</style>
